import debounce from 'lodash/debounce'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetActiveScheduledTasks } from 'src/api/scheduledTask'
import { IMoveReportDestinationPickerProps, IMoveReportTarget } from 'src/generated-types/form-schema'
import { getFormattedDate } from 'src/utils'
import { MoveReportTargetList } from './MoveReportTargetList'

const WORK_ORDER_PAGE_SIZE = 50

export const MoveReportWorkOrderPicker: React.FC<IMoveReportDestinationPickerProps> = ({
  customerId,
  excludedId,
  localityId,
  selected,
  onSelect,
}) => {
  const { t } = useTranslation(['form-schema-report', 'task'])
  const [searchText, setSearchText] = useState('')
  const [search, setSearch] = useState('')

  const debouncedSearch = useMemo(() => debounce((value: string) => setSearch(value), 400), [])

  const { data, isLoading } = useGetActiveScheduledTasks(localityId as EntityId, customerId, {
    key: search || undefined,
    page: 0,
    pageSize: WORK_ORDER_PAGE_SIZE,
  })

  const targets = useMemo<IMoveReportTarget[]>(
    () =>
      (data?.items ?? [])
        .filter(workOrder => String(workOrder?.id) !== String(excludedId))
        .map(workOrder => ({
          id: workOrder.id,
          label: workOrder.tasks?.title || t('untitled-work-order', { ns: 'form-schema-report' }),
          secondaryLabel: [workOrder.startDate, workOrder.endDate]
            .filter(Boolean)
            .map(date => getFormattedDate(new Date(date as string)))
            .join(' – '),
        })),
    [data, excludedId, t],
  )

  return (
    <MoveReportTargetList
      searchText={searchText}
      onSearchTextChange={value => {
        setSearchText(value)
        debouncedSearch(value)
      }}
      searchPlaceholder={t('search-work-order', { ns: 'form-schema-report' })}
      targets={targets}
      isLoading={isLoading}
      emptyText={t('no-work-order-found', { ns: 'form-schema-report' })}
      selected={selected}
      onSelect={onSelect}
      loadedCount={targets.length}
      totalCount={Math.max((data?.totalCount ?? 0) - 1, 0)}
    />
  )
}
